/**
 * Wave 3.84 — Karigar Form dialog
 *
 * Lets the operator generate a print-ready PDF that captures everything
 * a karigar (goldsmith) needs for the order:
 *   - order + goldsmith header, title, gold purity, ETA
 *   - green Description callout, red Special Comments callout
 *   - a subset of reference images (multi-selected by thumbnail)
 *   - a Requirements table (Balance-only OR full breakdown, toggleable)
 *   - a Milestones table (CAD/CAM/Chamka/Ghat)
 *
 * The dialog itself is purely a "capture user intent + POST + open PDF"
 * wrapper — the actual layout lives in `/api/orders/{id}/karigar-form/pdf`.
 *
 * Design decisions:
 *   • Default state: every image selected, "Balance only", branding ON.
 *   • Preview button opens the PDF in a new tab (blob URL).
 *   • Download button forces a save with a sane filename.
 *   • PDF attachments show a distinct badge because the backend renders
 *     their first page automatically via PyMuPDF, so the karigar sees a
 *     recognisable thumbnail on paper.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { FileText, Download, Eye, Loader2, Images } from 'lucide-react';

export function KarigarFormDialog({ open, onOpenChange, order }) {
  const images = order?.images || [];
  const thumbs = order?.images_thumb || [];
  const titles = order?.image_titles || [];

  // Local state — reset whenever the dialog opens.
  const [selected, setSelected] = useState({}); // { idx: bool }
  const [balanceOnly, setBalanceOnly] = useState(true);
  const [withBranding, setWithBranding] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      // Default: select every attachment.
      const init = {};
      images.forEach((_, i) => { init[i] = true; });
      setSelected(init);
      setBalanceOnly(true);
      setWithBranding(true);
      setBusy(false);
    }
  }, [open, images.length]);

  const selectedCount = useMemo(
    () => Object.values(selected).filter(Boolean).length,
    [selected],
  );

  const toggleImage = (idx) => {
    setSelected((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };
  const toggleAll = (val) => {
    const next = {};
    images.forEach((_, i) => { next[i] = val; });
    setSelected(next);
  };

  const fetchPdf = async () => {
    const image_indexes = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => Number(k));
    const res = await api.post(
      `/orders/${order.id}/karigar-form/pdf`,
      { image_indexes, balance_only: balanceOnly, with_branding: withBranding },
      { responseType: 'blob' },
    );
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const onPreview = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Pop-up blocked — download instead so the operator still gets it.
        const a = document.createElement('a');
        a.href = url;
        a.download = `JobCard_${(order.order_no || order.id)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.info('Pop-up blocked — file downloaded instead');
      }
      // Revoke after a delay so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate Job Card'));
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeNo = (order.order_no || order.id || 'order').replace(/[\\/]/g, '-');
      a.download = `JobCard_${safeNo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success('Job Card downloaded');
    } catch (e) {
      toast.error(extractError(e, 'Failed to download Job Card'));
    } finally {
      setBusy(false);
    }
  };

  const renderTile = (src, thumb, idx) => {
    // Determine attachment kind for the badge.
    const isPdf = typeof src === 'string' && src.startsWith('data:application/pdf');
    const isImg = typeof src === 'string' && src.startsWith('data:image/');
    const title = (titles[idx] || '').trim();
    const previewSrc = (isImg && (thumb || src)) || null;
    return (
      <button
        key={idx}
        type="button"
        onClick={() => toggleImage(idx)}
        data-testid={`karigar-image-toggle-${idx}`}
        className={`group relative flex flex-col items-stretch overflow-hidden rounded-md border transition ${
          selected[idx]
            ? 'border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50/40'
            : 'border-slate-200 hover:border-slate-400 bg-white'
        }`}
      >
        <div className="relative aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt={title || `Image ${idx + 1}`}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-500 text-[10px] uppercase tracking-wider">
              <FileText size={22} className="mb-1" />
              {isPdf ? 'PDF' : 'File'}
            </div>
          )}
          <div className="absolute left-1.5 top-1.5">
            <div
              className={`h-4 w-4 rounded-sm flex items-center justify-center border ${
                selected[idx]
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'bg-white/80 border-slate-300 text-transparent'
              }`}
            >
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6.2L4.6 8.8 10 3.4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {isPdf && (
            <div className="absolute right-1.5 top-1.5 text-[9px] font-semibold rounded bg-rose-600 text-white px-1.5 py-0.5 uppercase tracking-wider">
              PDF · pg 1
            </div>
          )}
        </div>
        <div className="p-1.5 text-[11px] text-slate-700 truncate text-left" title={title}>
          {title || <span className="italic text-slate-400">Untitled</span>}
        </div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="karigar-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} /> Job Card
          </DialogTitle>
          <DialogDescription>
            Generate a print-ready job card for the karigar with images, requirements,
            and production milestones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Options row */}
          <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="karigar-balance-only"
                  checked={balanceOnly}
                  onCheckedChange={setBalanceOnly}
                  data-testid="karigar-balance-only-switch"
                />
                <Label htmlFor="karigar-balance-only" className="cursor-pointer text-sm">
                  Show only Balance columns
                  <span className="block text-[11px] text-slate-500">
                    Off → also print Required &amp; Issued
                  </span>
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="karigar-branding"
                  checked={withBranding}
                  onCheckedChange={setWithBranding}
                  data-testid="karigar-branding-switch"
                />
                <Label htmlFor="karigar-branding" className="cursor-pointer text-sm">
                  Include company branding
                  <span className="block text-[11px] text-slate-500">
                    Logo, name, address, GSTIN
                  </span>
                </Label>
              </div>
            </div>
          </div>

          {/* Image selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Images size={16} className="text-slate-500" />
                <Label className="text-sm">
                  Attachments{' '}
                  <span className="text-slate-500 font-normal">
                    ({selectedCount} of {images.length} selected)
                  </span>
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleAll(true)}
                  disabled={!images.length || selectedCount === images.length}
                  data-testid="karigar-select-all-button"
                >
                  Select all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleAll(false)}
                  disabled={!selectedCount}
                  data-testid="karigar-clear-selection-button"
                >
                  Clear
                </Button>
              </div>
            </div>
            {images.length === 0 ? (
              <div className="rounded border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
                No attachments on this order. The form will still include order details,
                requirements, and milestones.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {images.map((src, idx) => renderTile(src, thumbs[idx], idx))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="karigar-cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={onPreview}
            disabled={busy}
            data-testid="karigar-preview-button"
          >
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Eye size={14} className="mr-1.5" />}
            Preview
          </Button>
          <Button
            onClick={onDownload}
            disabled={busy}
            className="luxury-button"
            data-testid="karigar-download-button"
          >
            {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default KarigarFormDialog;
